import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, Brain, Users, Sparkles } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { BlobShape, LeafBranch, FloralAccent } from "../components/Organic";
import { PHILOSOPHY_PILLARS } from "../lib/content";

const PILLAR_ICONS = [Heart, Sparkles, Brain];

export default function About() {
  return (
    <div data-testid="page-about">
      <Header />
      <PersonCentered />
      <TherapeuticApproach />
      <NeurodiversitySection />
      <WhoIWorkWith />
      <CtaSection />
    </div>
  );
}

function Header() {
  return (
    <section className="relative pt-44 md:pt-52 pb-16 md:pb-20 px-6 md:px-10 overflow-hidden">
      <BlobShape
        className="absolute -top-20 -left-32 w-[520px] h-[520px]"
        color="#8DAA91"
        opacity={0.2}
      />
      <FloralAccent
        className="absolute top-24 right-16 w-32 h-32 hidden md:block"
        color="#E4BE9E"
        opacity={0.4}
      />
      <div className="relative max-w-5xl mx-auto text-center">
        <Reveal>
          <span className="overline">About My Practice</span>
        </Reveal>
        <Reveal delay={1}>
          <h1 className="h-display mt-5 text-5xl md:text-6xl lg:text-7xl">
            Therapy that{" "}
            <span className="h-italic-accent">honors who you are.</span>
          </h1>
        </Reveal>
        <Reveal delay={2}>
          <p className="mt-7 text-lg md:text-xl text-[var(--at-navy-soft)] max-w-3xl mx-auto leading-relaxed">
            <span className="font-semibold text-[var(--at-navy)]">
              All Thrive Therapy and Consulting, PLLC
            </span>{" "}
            offers a warm, supportive, and nonjudgmental space where children,
            teens, adults, and caregivers can explore their concerns, build on
            their strengths, and work toward meaningful change.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function PersonCentered() {
  return (
    <section
      data-testid="about-person-centered"
      className="py-20 md:py-28 px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-[var(--at-teal)]/15 blob" />
              <img
                src="https://images.unsplash.com/photo-1573496546038-82f9c39f6365?auto=format&fit=crop&w=1000&q=85"
                alt="Therapist and client sharing a warm, candid conversation"
                className="relative w-full h-[460px] md:h-[560px] object-cover mask-organic-2 shadow-[0_30px_60px_-30px_rgba(26,46,68,0.35)]"
              />
              <div className="absolute -bottom-5 -right-5 bg-[var(--at-bg)] rounded-2xl px-5 py-4 shadow-xl border border-[var(--at-border)] max-w-[230px]">
                <p className="overline mb-1">My Philosophy</p>
                <p className="font-serif text-[var(--at-navy)] text-base leading-snug">
                  Every person has the capacity for growth when given a safe,
                  authentic space.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <span className="overline">My Therapeutic Approach</span>
            <h2 className="h-display mt-4 text-4xl md:text-5xl">
              Grounded in{" "}
              <span className="h-italic-accent">Person-Centered</span> Therapy.
            </h2>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-[var(--at-navy-soft)]">
              <p>
                My work is grounded in Person-Centered Therapy, which
                emphasizes the importance of a genuine, trusting therapeutic
                relationship. I believe that each person has the capacity for
                growth, healing, and self-understanding when provided with a
                safe and authentic space.
              </p>
              <p>
                Because every individual is unique, therapy is tailored to{" "}
                <em className="text-[var(--at-navy)] not-italic font-semibold">
                  your
                </em>{" "}
                goals and experiences rather than following a one-size-fits-all
                model.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function TherapeuticApproach() {
  return (
    <section
      data-testid="about-approach"
      className="relative py-24 md:py-32 px-6 md:px-10 bg-[var(--at-bg-2)] overflow-hidden noise"
    >
      <LeafBranch
        className="absolute top-12 right-8 w-40 h-40 hidden md:block -scale-x-100"
        color="#8DAA91"
        opacity={0.4}
      />
      <div className="relative max-w-6xl mx-auto">
        <Reveal>
          <div className="max-w-3xl">
            <span className="overline">Evidence-Based Modalities</span>
            <h2 className="h-display mt-4 text-4xl md:text-5xl">
              Methods that support{" "}
              <span className="h-italic-accent">lasting change.</span>
            </h2>
            <p className="mt-6 text-lg text-[var(--at-navy-soft)] leading-relaxed">
              My therapeutic approach is integrative and collaborative,
              blending Person-Centered Therapy, Cognitive Behavioral Therapy
              (CBT), and Dialectical Behavior Therapy (DBT) with
              evidence-based interventions tailored to each client's unique
              needs. Depending on your goals, therapy may also incorporate
              mindfulness, Acceptance and Commitment Therapy (ACT),
              Solution-Focused Therapy, Motivational Interviewing,
              trauma-informed care, self-compassion practices, behavioral
              activation, psychoeducation, stress management techniques,
              strengths-based interventions, and wellness strategies to
              support lasting emotional health and personal growth.
            </p>
          </div>
        </Reveal>

        <StaggerGroup className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PHILOSOPHY_PILLARS.map((p, i) => {
            const Icon = PILLAR_ICONS[i] || Heart;
            return (
              <StaggerItem key={p.title}>
                <div
                  data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="bento-card h-full rounded-[1.75rem] bg-[var(--at-bg)] border border-[var(--at-border)] p-8"
                >
                  <div className="w-12 h-12 rounded-2xl bg-[var(--at-teal-tint)] flex items-center justify-center text-[var(--at-teal)]">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-[var(--at-navy)] leading-snug">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[var(--at-navy-soft)] leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerGroup>
      </div>
    </section>
  );
}

function NeurodiversitySection() {
  return (
    <section
      data-testid="about-neurodiversity"
      className="py-24 md:py-32 px-6 md:px-10"
    >
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <Reveal>
            <span className="overline">Neurodiversity-Affirming Care</span>
            <h2 className="h-display mt-4 text-4xl md:text-5xl">
              Honoring how your{" "}
              <span className="h-italic-accent">unique brain</span> works.
            </h2>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-[var(--at-navy-soft)]">
              <p>
                I am committed to providing care that honors and celebrates
                neurodiversity. Each person experiences and interacts with the
                world differently, and therapy should reflect that.
              </p>
              <p>
                My goal is to help clients — especially those with ADHD or
                other neurodivergent experiences — develop self-awareness,
                self-compassion, and practical strategies to thrive in
                environments that may not always be designed with their needs
                in mind.
              </p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <Reveal delay={1}>
            <div className="relative">
              <div className="absolute -top-6 -right-6 w-36 h-36 bg-[var(--at-olive)]/30 blob" />
              <img
                src="https://images.unsplash.com/photo-1610216705422-caa3fcb6d158?auto=format&fit=crop&w=1000&q=85"
                alt="A diverse multigenerational family connecting outdoors"
                className="relative w-full h-[460px] md:h-[540px] object-cover mask-organic-3 shadow-[0_30px_60px_-30px_rgba(26,46,68,0.35)]"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WhoIWorkWith() {
  const groups = [
    { n: "01", title: "Children", body: "Big emotions, school stress, sensory needs." },
    { n: "02", title: "Teens", body: "Identity, anxiety, ADHD, peer dynamics." },
    { n: "03", title: "Adults", body: "Depression, anxiety, transitions, ADHD support." },
    { n: "04", title: "Caregivers", body: "Burnout prevention, boundaries, family support." },
  ];
  return (
    <section
      data-testid="about-who"
      className="py-24 md:py-32 px-6 md:px-10 bg-[var(--at-bg-2)]"
    >
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto">
            <span className="overline">Who I Work With</span>
            <h2 className="h-display mt-4 text-4xl md:text-5xl">
              Walking alongside you on the{" "}
              <span className="h-italic-accent">journey ahead.</span>
            </h2>
            <p className="mt-6 text-lg text-[var(--at-navy-soft)] leading-relaxed">
              I support people facing a variety of emotional, behavioral,
              relational, and life-transition concerns. Whether you're seeking
              therapy for yourself, your child, or your family, I welcome the
              opportunity to walk alongside you toward greater well-being,
              connection, and personal growth.
            </p>
          </div>
        </Reveal>

        <StaggerGroup className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5">
          {groups.map((g) => (
            <StaggerItem key={g.n}>
              <div className="h-full rounded-[1.5rem] p-6 bg-[var(--at-bg)] border border-[var(--at-border)]">
                <p className="font-serif text-4xl text-[var(--at-teal)] leading-none">
                  {g.n}
                </p>
                <h3 className="mt-4 font-serif text-xl text-[var(--at-navy)] leading-snug">
                  {g.title}
                </h3>
                <p className="mt-2 text-sm text-[var(--at-navy-soft)] leading-relaxed">
                  {g.body}
                </p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section data-testid="about-cta" className="px-6 md:px-10 pb-24 pt-16">
      <div className="max-w-5xl mx-auto rounded-[2.5rem] bg-[var(--at-navy)] text-[var(--at-bg)] p-10 md:p-16 text-center relative overflow-hidden">
        <BlobShape
          className="absolute -top-24 -right-20 w-[380px] h-[380px]"
          color="#2A7B76"
          opacity={0.3}
        />
        <div className="relative">
          <Reveal>
            <h2 className="h-display text-3xl md:text-5xl text-[var(--at-bg)]">
              Ready to feel{" "}
              <span className="italic text-[var(--at-olive)]">heard?</span>
            </h2>
            <p className="mt-5 text-[var(--at-bg)]/80 max-w-xl mx-auto">
              Book a free, no-obligation consultation. I'll talk through what
              you need and see if I'm a good fit.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="btn-primary mt-8 bg-[var(--at-olive)] text-[var(--at-navy)] hover:bg-[var(--at-bg)]"
            >
              Start the Conversation <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
